import { MutableRefObject, useCallback, useEffect, useRef } from "react";
import { useAppDispatch } from "@/app/hooks";
import { fetchByCriteria } from "@/app/reducers/properties";
import { IFilterContext } from "@/app/globalContext/filterContext.interface";
import { IFiltros } from "@/components/templates/search/interfaces/IEstructuraPrincipal";

interface UsePropertySearchOptions {
	/**
	 * Delay en milisegundos para búsquedas después de la primera
	 * @default 800
	 */
	actionDelay?: number;

	/**
	 * Delay en milisegundos para la primera búsqueda
	 * @default 0
	 */
	firstSearchDelay?: number;

	/**
	 * Valor serializado inicial del filtro (para comparación inicial)
	 * @default ""
	 */
	initialFilterStateSerialized?: string;

	/**
	 * Si debe ejecutarse automáticamente cuando cambian los filtros
	 * @default true
	 */
	autoExecute?: boolean;

	/**
	 * Ref a filtros extra que se inyectan en cada dispatch (ej: viewboard bbox en vista mapa).
	 * Se lee en el momento del dispatch — no necesita estar en deps.
	 */
	extraFiltrosRef?: MutableRefObject<IFiltros[]>;
}

interface UsePropertySearchReturn {
	/**
	 * Función para ejecutar la búsqueda manualmente
	 */
	fetchByCriteriaCallback: (timeout?: number) => void;

	/**
	 * Ref al timeout actual (útil para cancelar desde componentes externos)
	 */
	triggerSearchRef: React.MutableRefObject<NodeJS.Timeout | undefined>;

	/**
	 * Ref al último dispatch en vuelo. Llamar .abort() cancela la petición en curso.
	 */
	pendingRef: MutableRefObject<{ abort: () => void } | null>;
}

/**
 * Hook personalizado que maneja la búsqueda de propiedades con debounce,
 * detección de query params y prevención de búsquedas duplicadas
 */
export const usePropertySearch = (
	filterState: IFilterContext,
	filterStateSerialized: string,
	options: UsePropertySearchOptions = {}
): UsePropertySearchReturn => {
	const {
		actionDelay = 800,
		firstSearchDelay = 0,
		initialFilterStateSerialized = "",
		autoExecute = true,
	} = options;

	const dispatch = useAppDispatch();
	const filterValuePrev = useRef(initialFilterStateSerialized);
	const triggerSearch = useRef<NodeJS.Timeout | undefined>(undefined);
	const hasExecutedFirstSearch = useRef<boolean>(false);
	const pendingRef = useRef<{ abort: () => void } | null>(null);

	const fetchByCriteriaCallback = useCallback(
		(timeout?: number) => {
			const newFilterStateSerialized =
				filterStateSerialized + "" + filterState.order;

			// Force first search execution regardless of comparison
			const isFirstSearch = !hasExecutedFirstSearch.current;

			if (
				!isFirstSearch &&
				filterValuePrev.current === newFilterStateSerialized
			) {
				return;
			}

			if (triggerSearch.current !== undefined) {
				clearTimeout(triggerSearch.current);
			}

			const timeoutToUse =
				timeout !== undefined
					? timeout
					: hasExecutedFirstSearch.current
						? actionDelay
						: firstSearchDelay;

			triggerSearch.current = setTimeout(() => {
				// Inyectar filtros extra (ej: viewboard bbox en vista mapa) sin duplicar
				const extraFiltros = options.extraFiltrosRef?.current ?? [];
				const baseFiltros = extraFiltros.length > 0
					? filterState.filtros.filter(f => !extraFiltros.some(ef => ef.id === f.id))
					: filterState.filtros;
				if (pendingRef.current) pendingRef.current.abort();
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				pendingRef.current = dispatch(fetchByCriteria({
					...filterState,
					filtros: [...baseFiltros, ...extraFiltros],
					page: 1,
				})) as any;
				// Mark that we've executed the first search
				hasExecutedFirstSearch.current = true;
				filterValuePrev.current = newFilterStateSerialized;
				if (triggerSearch.current !== undefined) {
					clearTimeout(triggerSearch.current);
				}
			}, timeoutToUse);
		},
		[dispatch, filterState]
	);

	useEffect(() => {
		if (!autoExecute) {
			return;
		}

		// Check if there are query parameters that might still need to be parsed
		const hasQueryParams =
			typeof window !== "undefined" && window.location.search.includes("=");

		// Smart detection: compare URL query params with parsed filters
		let shouldWaitForQueryParsing = false;
		if (
			!hasExecutedFirstSearch.current &&
			hasQueryParams &&
			typeof window !== "undefined"
		) {
			const urlParams = new URLSearchParams(window.location.search);
			const queryParamKeys = Array.from(urlParams.keys()).filter(
				(key) => key !== "o" && key !== "moneda" && key !== "pagina"
			); // Exclude tracking params and non-filter URL params
			const parsedFilterIds = filterState.filtros.map((f) => f.id);
			// Check if there are query params that haven't been parsed into filters yet
			const unparsedParams = queryParamKeys.filter(
				(paramKey) => !parsedFilterIds.includes(paramKey)
			);
			shouldWaitForQueryParsing = unparsedParams.length > 0;
		}

		const delayToUse = hasExecutedFirstSearch.current
			? actionDelay
			: shouldWaitForQueryParsing
				? 200
				: firstSearchDelay;

		fetchByCriteriaCallback(delayToUse);
	}, [filterStateSerialized, filterState, fetchByCriteriaCallback]);

	// Cleanup
	useEffect(() => {
		return () => {
			if (triggerSearch.current !== undefined) {
				clearTimeout(triggerSearch.current);
			}
		};
	}, []);

	return {
		fetchByCriteriaCallback,
		triggerSearchRef: triggerSearch,
		pendingRef,
	};
};
